// @migration-in-progress
/**
 * Context shim for cloud-ready operations
 * Provides centralized context for user, vessel, and company information
 * Will be replaced with actual auth/context when cloud sync is implemented
 */

/**
 * Get current context for operations
 * @param {string} vesselId - The current vessel ID (passed from React component)
 * @returns {Object} Context object with user, vessel, and company info
 */
export const getContext = (vesselId = 'default') => {
  // Read from RBAC user storage (bridged from AuthProvider)
  let userName = 'Unknown User';
  let userId = 'current-user';
  let userRole = 'master';
  let organizationName = '';  // FT-119: Read org name from current_user

  try {
    const userStr = localStorage.getItem('current_user');
    if (userStr) {
      const user = JSON.parse(userStr);
      userName = user?.name || 'Unknown User';
      userId = user?.id || 'current-user';
      userRole = user?.role || 'master';
      organizationName = user?.organizationName || '';  // FT-119: Extract org name
    }
  } catch (e) {
    // Ignore parse errors
  }

  const companyId = localStorage.getItem('companyId') || 'default-company';

  return {
    // User context (from RBAC storage)
    userId: userId,
    userName: userName,
    userRole: userRole,

    // Vessel context
    vesselId: vesselId,
    vesselName: localStorage.getItem('name') || 'Default Vessel',

    // Company context
    companyId: companyId,
    companyName: organizationName,  // FT-119: Use org name from current_user instead of stale localStorage key
    
    // Timestamp
    timestamp: new Date().toISOString(),
    
    // Helper to check permissions (placeholder)
    canEdit: true, // Will check actual permissions
    canDelete: true, // Will check actual permissions
    isAdmin: false // Will check actual role
  };
};

/**
 * Get current vessel ID
 * @returns {string} Current vessel ID
 */
export const getCurrentVesselId = () => {
  return getContext().vesselId;
};

/**
 * Get current company ID
 * @returns {string} Current company ID
 */
export const getCurrentCompanyId = () => {
  return getContext().companyId;
};

/**
 * Get current user ID
 * @returns {string} Current user ID
 */
export const getCurrentUserId = () => {
  return getContext().userId;
};

/**
 * Check if user has permission for an action
 * @param {string} action - Action to check (edit, delete, approve, etc.)
 * @param {Object} record - Record to check permissions against
 * @returns {boolean} Whether user has permission
 */
export const hasPermission = (action, record = null) => {
  const context = getContext();
  
  // For now, simple permission check
  // Will be replaced with proper RBAC when auth is implemented
  switch (action) {
    case 'edit':
      return context.canEdit;
    case 'delete':
      return context.canDelete;
    case 'approve':
      return context.isAdmin || context.userRole === 'master';
    default:
      return true;
  }
};